
import { Vue, Component } from "vue-property-decorator";
import { AlarmQueueFilter, AlarmQueueFilterEditModel } from "@/store/eventqueue/types";
import { Getter, namespace } from "vuex-class";
import { FeaturesList, UserPermissions } from "@/store/types";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import AlarmQueueFilterEdit from "@/components/AlarmQueueFilterEdit.vue";

const EventQueue = namespace("eventqueue");
const Subscription = namespace("subscription");

@Component({
	components: {
		"alarm-queue-filter-edit": AlarmQueueFilterEdit,
		VuePerfectScrollbar: VuePerfectScrollbar
	}
})
export default class AlarmQueueFilterTabs extends Vue {

	@Getter getPermissions: UserPermissions;
	@Getter getFeaturesList: FeaturesList;

	@Subscription.Getter isSubscriptionActive: boolean;
	@EventQueue.Getter("getAlarmQueueFilters") activeFilters!: AlarmQueueFilter[];
	@EventQueue.Getter getHandlingEvent: boolean;
	@EventQueue.Action fetchAlarmQueueFilters: () => Promise<void>;
	@EventQueue.Action loadAvailableAlarmQueueFilters: () => Promise<AlarmQueueFilter[]>;
	@EventQueue.Action addAlarmQueueFilter: (params: object) => Promise<void>;
	@EventQueue.Action removeAlarmQueueFilter: (params: object) => Promise<void>;
	@EventQueue.Action deleteAlarmQueueFilter: (id: number) => Promise<void>;
	@EventQueue.Action setActiveFilter: (params: object) => Promise<void>;

	@EventQueue.Mutation setNewFilterModalShown: any;

	// Public to expose to tests
	public allAvailableFilters: AlarmQueueFilter[] = [];
	public filterSearch: string = "";
	public addingFilter = false;
	public filterToDelete = {};
	public editedFilter: AlarmQueueFilterEditModel = null;
	public modalShow = false;

	public async mounted(): Promise<void> {
		this.hideConfirmationModal();
		await this.fetchAlarmQueueFilters();
		await this.setAllAvailableFilters();
	}

	public async removeFilter(filter: AlarmQueueFilter): Promise<void> {
		const filterId = filter.alarmQueueFilterID;
		const regionId = filter.regionID || 0;

		await this.removeAlarmQueueFilter({ filterId, regionId });
	}

	private async setAllAvailableFilters(): Promise<void> {
		const filters = await this.loadAvailableAlarmQueueFilters();
		this.$set(this, "allAvailableFilters", filters);
	}

	public editFilter(filter: AlarmQueueFilter): void {
		// @techdebt @todo delete after serverside implemetation
		const editedFilter: AlarmQueueFilterEditModel = {
			alarmQueueFilterID: filter.alarmQueueFilterID,
			title: filter.title,
			priorityValue: filter.priorityHigherThan,
			priorityOperator: this.capitalizeFirstLetter(filter.priorityHigherThanDDL),
			numberOfEventsValue: filter.numberOfEvents,
			numberOfEventsOperator: this.capitalizeFirstLetter(filter.numberOfEventsDDL),
			alarmAgeValue: filter.olderThan,
			alarmAgeOperator: this.capitalizeFirstLetter(filter.olderThanDDL),
			responseAlarmTypeIds: filter.responseAlarmTypeIds,
			groupID: filter.groups.length ? filter.groups[0].groupID : null,
			shared: filter.shared,
			created: filter.created,
			combinedFilters: filter.combinedFilters,
			groups: filter.groups
		};

		this.$set(this, "editedFilter", editedFilter);
		this.setNewFilterModalShown(true);
	}

	public async deleteFilter(id: number): Promise<void> {
		await this.deleteAlarmQueueFilter(id);
		this.hideConfirmationModal();
		await this.setAllAvailableFilters();
	}

	public selectFilter(filter: AlarmQueueFilter): void {
		// when changing filters clear out any selected rows
		this.$emit('clearSelectedAlarms');

		// Update the store with the details of the filter
		const data = {
			filterId: filter.alarmQueueFilterID,
			regionId: filter.regionID
		};
		this.setActiveFilter(data);
	}

	public resetFilterSearch(): void {
		this.filterSearch = "";
	}

	public moveFilter(forward: boolean): void {
		if (!this.activeFilters || !this.activeFilters.length || this.activeFilters.length == 1)
			return;

		const indexOfCurrent = this.visibleFilters.findIndex(x => x.isActive);
		const filterLength = this.visibleFilters.length - 1;
		let newIndex = forward ? (indexOfCurrent + 1) : (indexOfCurrent - 1);
		newIndex = newIndex < 0 ? (0) : (newIndex > filterLength ? filterLength : newIndex);
		this.selectFilter(this.activeFilters[newIndex]);
	}

	public confirmDeleteFilter(filter: AlarmQueueFilter): void {
		this.filterToDelete = filter;
		this.modalShow = true;
	}

	// Gets the filters and sorts then alphabetically
	public get filteredFilters(): AlarmQueueFilter[] {
		const titleWithRegion = (filter: AlarmQueueFilter) =>
			filter.regionID > 0 ? `${filter.title} (${filter.regionTitle})` : filter.title;
		const search = this.filterSearch.toLowerCase();
		return this.availableFilters
			.filter(
				filter =>
					titleWithRegion(filter)
						.toLowerCase()
						.indexOf(search) > -1
			)
			.sort(function(a, b) {
				const first = a.title.toUpperCase();
				const second = b.title.toUpperCase();
				return first < second ? -1 : first > second ? 1 : 0;
			});
	}

	public get visibleFilters(): AlarmQueueFilter[] {
		return this.activeFilters.filter(filter => !filter.hide);
	}

	public get availableFilters(): AlarmQueueFilter[] {
		return this.allAvailableFilters.filter(queueFilter => {
			return (
				this.activeFilters.findIndex(
					activeFilter =>
						activeFilter.alarmQueueFilterID === queueFilter.alarmQueueFilterID &&
						activeFilter.regionID === queueFilter.regionID
				) == -1
			);
		});
	}

	public async newFilterSelected(filter: AlarmQueueFilter): Promise<void> {
		this.addingFilter = true;

		await this.addAlarmQueueFilter({
			filterId: filter.alarmQueueFilterID,
			regionId: filter.regionID || null
		});

		this.addingFilter = false;

		this.activeFilters.forEach(activeFilter => {
			if (activeFilter.alarmQueueFilterID == filter.alarmQueueFilterID &&
				activeFilter.regionID == filter.regionID) {
				this.selectFilter(activeFilter);
			}
		});
	}

	public onCancelFilterEditing(): void {
		this.editedFilter = null;
	}

	public hideConfirmationModal(): void {
		this.modalShow = false;
	}

	public capitalizeFirstLetter(str: string): string {
		return str ? str.charAt(0).toUpperCase() + str.slice(1) : str;
	}
}
